import { HttpEvent, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { toast } from 'ngx-sonner';
import { catchError, NEVER, Observable, throwError } from 'rxjs';

const getErrorTexts = (statusCode: HttpStatusCode, message: string | undefined, translate: TranslateService) => {
    switch (statusCode) {
        case 401:
            return {
                title: translate.instant('UTIL_ERRORS.HTTP_401.TITLE'),
                description: message ?? translate.instant('UTIL_ERRORS.HTTP_401.DESCRIPTION')
            };
        case 403:
            return {
                title: translate.instant('UTIL_ERRORS.HTTP_403.TITLE'),
                description: message ?? translate.instant('UTIL_ERRORS.HTTP_403.DESCRIPTION')
            };
        case 404:
            return {
                title: translate.instant('UTIL_ERRORS.HTTP_404.TITLE'),
                description: message ?? translate.instant('UTIL_ERRORS.HTTP_404.DESCRIPTION')
            };
        case 409:
            return {
                title: translate.instant('UTIL_ERRORS.HTTP_409.TITLE'),
                description: message
            };
        case 429:
            return {
                title: translate.instant('UTIL_ERRORS.HTTP_429.TITLE'),
                description: translate.instant('UTIL_ERRORS.HTTP_429.DESCRIPTION')
            };
        default:
            return {
                title: translate.instant('UTIL_ERRORS.HTTP_4XX.TITLE', { status: statusCode }),
                description: message
            };
    }
};

/**
 * Catches errors and shows a toast with the error message.
 *
 * @param apiSegment - The starting segment of the API URL. Only requests starting with this segment will be intercepted
 */
export const errorInterceptor: (apiSegment?: string) => HttpInterceptorFn =
    (apiSegment?: string) =>
    (req, next): Observable<HttpEvent<unknown>> => {
        if (apiSegment && !req.url.startsWith(apiSegment)) {
            return next(req);
        }

        const translate = inject(TranslateService);

        return next(req).pipe(
            catchError((error, caught) => {

                if (error.status >= 400) {
                    const message = (typeof error.error === 'object' ? error.error?.message : error.error) ?? error.statusText;
                    const { title, description } = getErrorTexts(error.status, message, translate);

                    toast.error(title,
                        {
                            description,
                            duration: 6000
                        }
                    );

                    throw error;
                }

                return caught;
            })
        );
    };
